import {
  AbstractChatService,
  ChatMessageSearchOccurrence,
  ChatMessageSearchOccurrenceField,
  ChatResponse,
  ChatsNew,
  FindAllUserRequestDto,
  UserResponse,
  WebsocketService
} from '@aionzdev/modules';
import { HttpClient, HttpResponse, HttpEventType } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject, merge, of, throwError } from 'rxjs';
import { map, switchMap, filter, tap, catchError, take } from 'rxjs/operators';
import { ChatApiService, ChatEmpresaItem } from './api/chat-api.service';
import { environment } from '../../../environments/environment';
import { AI_STREAM_SOCKET_EVENTS, AIStreamEvent } from '../models/ai-stream.interface';
import { Message, MessageWebSocket, TypesMessage } from '../models/chat.interface';
import { AuthModuleService } from './auth-module.service';

interface ChatMessageSearchResponse {
  requestId: string;
  occurrences: ChatMessageSearchOccurrence[];
  error?: string;
}

interface MessageFromBackend {
  id?: string;
  idTemp: string;
  type: TypesMessage.TEXT | TypesMessage.DOC | TypesMessage.IMAGE | TypesMessage.SYSTEM | TypesMessage.SYSTEM_DATE;
  content: string;
  chatHash: string;
  from: number;
  to: number[];
  time?: string;
  /** Instante real da persistência (ISO). Fonte de verdade para ordenar. */
  createdAt?: string;
  status?: string;
  fileName?: string;
  fileSize?: string;
  title?: string;
  image?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ChatsService implements AbstractChatService {
  private readonly chatApi = inject(ChatApiService);
  private readonly websocket = inject(WebsocketService);
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthModuleService);

  private readonly reloadChatsSubject = new Subject<void>();
  reloadChats$: Observable<void> = this.reloadChatsSubject.asObservable();

  private participantsByChatId = new Map<string, Map<string, { name: string; avatar?: string }>>();
  private currentChatId: string | undefined;

  constructor() {
    this.interceptNewMessageEvent();
    this.authService.sessionCleared$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.resetSession());
  }

  /**
   * Descarta o estado de chat do usuário anterior (logout / troca de conta na
   * mesma aba). Ver docs/specs/limpeza-de-sessao-no-logout.md.
   *
   * Nota: o socket do `WebsocketService` (@aionzdev/modules) continua
   * autenticado com o token anterior — a lib não expõe reconexão. Registrado
   * como follow-up na spec (Bloco 5).
   */
  resetSession(): void {
    this.participantsByChatId.clear();
    this.currentChatId = undefined;
    localStorage.removeItem('ebarn_localChat');
  }

  /**
   * Evento do socket como Observable. Usa `listen$` da lib, que remove o
   * listener no unsubscribe: antes cada subscribe registrava um `socket.on`
   * que nunca saía, e eles se acumulavam a cada vez que o chat (sidenav) era
   * aberto de novo.
   */
  private fromWebsocketEvent<T>(event: string): Observable<T> {
    return this.websocket.listen$<T>(event);
  }

  /**
   * Turno do agente de IA em streaming no chat: os seis eventos `aiStream*`
   * normalizados num único fluxo, só do `chatHash` informado.
   */
  listenAIStream(chatHash: string): Observable<AIStreamEvent> {
    const eventos = (Object.keys(AI_STREAM_SOCKET_EVENTS) as AIStreamEvent['type'][]).map(type =>
      this.fromWebsocketEvent<Omit<AIStreamEvent, 'type'>>(AI_STREAM_SOCKET_EVENTS[type]).pipe(
        map(payload => ({ ...payload, type }) as AIStreamEvent)
      )
    );

    return merge(...eventos).pipe(filter(event => event?.chatHash === chatHash));
  }

  /**
   * Emite a cada reconexão do socket. Um socket novo não está mais nas rooms
   * do anterior, então quem estava num chat precisa entrar de novo.
   */
  onReconnect(): Observable<void> {
    return this.fromWebsocketEvent<void>('connect');
  }

  private interceptNewMessageEvent(): void {
    this.websocket.listen('new-message', (msg: MessageFromBackend) => {
      const mapped = this.mapToChatResponse(msg, this.currentChatId);
      this.websocket.sendMessage('new-message', mapped);
    });
  }

  getAllchats(params: { take: number; currentPage: number; search?: string; archived?: string }): Observable<ChatsNew[]> {
    return this.chatApi.listAllChats({
      search: params.search,
      page: params.currentPage,
      pageSize: params.take,
    }).pipe(map(res => res.data.map(chat => this.mapToChatsNew(chat))));
  }

  getAllchatsTenant(params: { take: number; currentPage: number; search: string }): Observable<ChatsNew[]> {
    return this.chatApi.listAllChats({
      search: params.search,
      page: params.currentPage,
      pageSize: params.take,
    }).pipe(map(res => res.data.map(chat => this.mapToChatsNew(chat))));
  }

  getChatsMessages(params: { chatId: string; currentPage: number; take: number }): Observable<ChatResponse[]> {
    const response$ = this.fromWebsocketEvent<MessageFromBackend[]>('returnAllMessages-admin').pipe(
      map(messages => messages
        .filter(msg => msg.type !== TypesMessage.SYSTEM_DATE)
        .map(msg => this.mapToChatResponse(msg, params.chatId))
        // Mais recentes primeiro (contrato do @aionzdev/modules, que faz
        // `.reverse()` ao exibir). `sentAt` já deriva do `createdAt` real.
        .sort((a, b) => {
          const diff = new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime();
          return diff !== 0 ? diff : String(b.id).localeCompare(String(a.id));
        })
      )
    );

    this.currentChatId = params.chatId;
    this.websocket.sendMessage('join', params.chatId);
    this.websocket.sendMessage('getAllMessages', {
      chatHash: params.chatId,
      page: params.currentPage,
      pageSize: params.take,
    });

    return response$;
  }

  archiveAndUnarchiveChatService(chatHash: string): Observable<void> {
    return this.chatApi.arquivarChat(chatHash);
  }

  getConsolidatedData(): ReturnType<AbstractChatService['getConsolidatedData']> {
    return this.chatApi.getIndicadores();
  }

  private mapToChatResponse(msg: MessageFromBackend, chatId?: string): ChatResponse {
    const contentTypeMap: Record<MessageFromBackend['type'], ChatResponse['contentType']> = {
      [TypesMessage.TEXT]: 'TEXT',
      [TypesMessage.DOC]: 'FILE',
      [TypesMessage.IMAGE]: 'IMAGE',
      [TypesMessage.SYSTEM]: 'SYSTEM',
      [TypesMessage.SYSTEM_DATE]: 'SYSTEM_DATE',
    };

    const senderId = String(msg.from);
    const participantFromMap = chatId ? this.participantsByChatId.get(chatId)?.get(senderId) : undefined;
    const senderName = msg.title || participantFromMap?.name || '';
    const senderAvatar = msg.image || participantFromMap?.avatar;

    return {
      id: msg.id ?? msg.idTemp,
      chatId: msg.chatHash,
      content: msg.content,
      contentType: contentTypeMap[msg.type] ?? 'TEXT',
      senderId,
      sender: {
        id: senderId,
        name: senderName,
        avatar: senderAvatar,
      },
      sentAt: this.parseMessageTime(msg.time, msg.createdAt),
      deletedById: null,
      reasonForDeletion: null,
    };
  }

  /**
   * Ordena as mensagens da forma como devem ser exibidas: mais antigas
   * primeiro, usando o timestamp real da persistência. Desempate estável por
   * `idTemp` para o caso de mensagens com o mesmo instante.
   */
  private sortByTimeAscending<T extends { idTemp?: string; time?: unknown; createdAt?: unknown }>(
    messages: T[]
  ): T[] {
    return [...messages].sort((a, b) => {
      const diff = this.resolveTimestamp(a) - this.resolveTimestamp(b);
      if (diff !== 0) return diff;
      return String(a.idTemp ?? '').localeCompare(String(b.idTemp ?? ''));
    });
  }

  private resolveTimestamp(msg: { time?: unknown; createdAt?: unknown }): number {
    for (const value of [msg.createdAt, msg.time]) {
      if (!value) continue;
      const parsed = new Date(value as string | number | Date).getTime();
      if (!isNaN(parsed)) return parsed;
    }
    return 0;
  }

  private getDateLabel(date: Date): string {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const isSameDay = (a: Date, b: Date) =>
      a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();

    if (isSameDay(date, today)) return 'Hoje';
    if (isSameDay(date, yesterday)) return 'Ontem';
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  }

  private parseMessageTime(time?: string | Date, createdAt?: string | Date): string {
    // `createdAt` (ISO real da persistência) tem prioridade — `time` do backend
    // pode vir só como "HH:mm", que não permite ordenar entre dias nem
    // desempatar mensagens do mesmo minuto.
    if (createdAt) {
      const fromCreatedAt = new Date(createdAt);
      if (!isNaN(fromCreatedAt.getTime())) return fromCreatedAt.toISOString();
    }

    if (!time) return new Date().toISOString();

    // If it's already a valid full ISO/datetime string (ou um Date), use direto
    const direct = new Date(time);
    if (!isNaN(direct.getTime())) return direct.toISOString();

    // Backend sends only "HH:mm" — combine with today's date to get a parseable value
    const today = new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"
    const combined = new Date(`${today}T${String(time)}:00`);
    if (!isNaN(combined.getTime())) return combined.toISOString();

    return new Date().toISOString();
  }

  private mapToChatsNew(chat: ChatEmpresaItem): ChatsNew {
    const participantsMap = new Map(chat.users.map(u => [String(u.id), { name: u.nome, avatar: u.avatar ?? undefined }]));
    this.participantsByChatId.set(chat.chatHash, participantsMap);

    return {
      id: chat.chatHash,
      type: 'DIRECT',
      title: chat.title,
      image: chat.image ?? '',
      createdAt: chat.createdAt,
      context: null,
      createdById: String(chat.users[0]?.id ?? ''),
      tenantId: '',
      createdBy: {
        id: String(chat.users[0]?.id ?? ''),
        name: chat.users[0]?.nome ?? '',
        avatar: chat.users[0]?.avatar ?? undefined,
      },
      participants: chat.users.map(u => ({
        id: String(u.id),
        name: u.nome,
        avatar: u.avatar ?? undefined,
      })),
      messages: [{
        id: '',
        content: chat.lastMessage,
        contentType: 'TEXT',
        sentAt: chat.updatedAt,
        senderId: '',
        sender: { id: '', name: '' },
        reasonForDeletion: null,
      }] as any,
    };
  }

  startChat(params: { userId: string }): Observable<ChatsNew> {
    const recipientIdNum = Number(params.userId);
    return this.chatApi.checkExistsChat([recipientIdNum]).pipe(
      switchMap(res => {
        const chatHash = res.chatHash;
        return this.getChatById(chatHash);
      })
    );
  }

  getChatById(chatHash: string): Observable<ChatsNew> {
    return this.chatApi.listAllChats({ page: 1, pageSize: 100 }).pipe(
      map(res => {
        const found = res.data.find(c => c.chatHash === chatHash);
        if (found) {
          return this.mapToChatsNew(found);
        }
        return {
          id: chatHash,
          type: 'DIRECT',
          title: 'Chat de Compras',
          image: '',
          createdAt: new Date().toISOString(),
          context: null,
          createdById: '',
          tenantId: '',
          createdBy: { id: '', name: '' },
          participants: [],
          messages: [] as any
        };
      })
    );
  }

  fileSizeFormatted(fileSize: number): string {
    if (fileSize < 1024) return `${fileSize} B`;
    if (fileSize < 1024 * 1024) return `${(fileSize / 1024).toFixed(1)} KB`;
    return `${(fileSize / (1024 * 1024)).toFixed(1)} MB`;
  }

  uploadFile(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);

    let fileType = 'document';
    if (file.type.startsWith('image/')) {
      fileType = 'image';
    } else if (file.type.startsWith('audio/')) {
      fileType = 'audio';
    } else if (file.type.startsWith('video/')) {
      fileType = 'video';
    }

    const url = `${environment.apiUrl}/chat/send-file/${this.currentChatId}/${fileType}`;
    return this.http.post<{ fileUrl: string }>(url, formData).pipe(
      map(res => res.fileUrl)
    );
  }

  deleteMessageTenant(_messageId: string, _reasonForDeletion: string): Observable<{ success: boolean }> {
    return of({ success: true });
  }

  groupMessagesByDate(messages: ChatResponse[], existingDates: string[] = []): ChatResponse[] {
    // existingDates may contain labels ("Hoje", "Ontem") — normalize them to DD/MM/YYYY keys
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const todayKey = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;
    const yesterdayKey = `${String(yesterday.getDate()).padStart(2, '0')}/${String(yesterday.getMonth() + 1).padStart(2, '0')}/${yesterday.getFullYear()}`;

    const seenExisting = new Set<string>();
    for (const label of existingDates) {
      if (label === 'Hoje') seenExisting.add(todayKey);
      else if (label === 'Ontem') seenExisting.add(yesterdayKey);
      else seenExisting.add(label); // already in DD/MM/YYYY format
    }

    const result: ChatResponse[] = [];

    for (const msg of messages) {
      if (msg.contentType === 'SYSTEM_DATE') continue; // backend separators are ignored

      const d = new Date(msg.sentAt);
      const date = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
      const label = this.getDateLabel(d);

      if (!seenExisting.has(date)) {
        seenExisting.add(date);
        result.push({
          id: `date-${date}`,
          chatId: msg.chatId,
          content: label,
          contentType: 'SYSTEM_DATE',
          senderId: 'system',
          sender: { id: 'system', name: 'System', avatar: '' },
          sentAt: msg.sentAt,
          deletedById: null,
          reasonForDeletion: null,
        });
      }
      result.push(msg);
    }

    // Remove any trailing SYSTEM_DATE separators with no messages after them
    while (result.length > 0 && result[result.length - 1].contentType === 'SYSTEM_DATE') {
      result.pop();
    }

    return result;
  }

  findAllUsers(_data: FindAllUserRequestDto): Observable<HttpResponse<UserResponse[]>> | any {
    throw new Error('Method not implemented.');
  }

  joinChat(chatHash: string): void {
    this.websocket.sendMessage('join', chatHash);
  }

  leaveChat(chatHash: string): void {
    this.websocket.sendMessage('leaveChat', chatHash);
  }

  sendMessage(message: MessageWebSocket): void {
    this.websocket.sendMessage('sendMessage', message);
  }

  getSavedMessage(): Observable<Message> {
    return this.fromWebsocketEvent<Message>('savedMessage');
  }

  getErrorMessage(): Observable<Message> {
    return this.fromWebsocketEvent<Message>('ErrorSaveMessage');
  }

  listenToAITypingState(_chatHash: string): Observable<{ isTyping: boolean }> {
    return this.fromWebsocketEvent<{ isTyping: boolean }>('aiTypingState').pipe(
      filter((event): event is { isTyping: boolean } => event && typeof event.isTyping === 'boolean'),
      map(event => ({ isTyping: event.isTyping }))
    );
  }

  updateAITypingState(chatHash: string, isTyping: boolean): void {
    this.websocket.sendMessage('aiTypingState', { chatHash, isTyping });
  }

  listenInfoWithinChat(chatHash: string): Observable<any> {
    return this.authService.me().pipe(
      switchMap(user => {
        const userId = user.id;
        this.websocket.sendMessage('getInfoWithinChat', { userId, chatHash });
        return this.fromWebsocketEvent<any>(`returnInfoWithinChat-${userId}`);
      }),
      tap(({ context, users, title, isGroup, isArchived }) => {
        const chatInfo = {
          chatHash,
          title,
          image: context?.image || '',
          users,
          context,
          isGroup,
          isArchived
        };
        localStorage.setItem('ebarn_localChat', JSON.stringify(chatInfo));
      })
    );
  }

  listenContextUpdatesWithinChat(_chatHash: string): Observable<any> {
    return this.fromWebsocketEvent<any>('returnContextUpdatedWithinChat').pipe(
      tap(({ context }) => {
        const localChatStr = localStorage.getItem('ebarn_localChat');
        if (localChatStr) {
          const localChat = JSON.parse(localChatStr);
          localChat.context = context;
          localStorage.setItem('ebarn_localChat', JSON.stringify(localChat));
        }
      })
    );
  }

  updateMessageReadStatus(message: Message, userId: number): void {
    this.websocket.sendMessage('updateMessageReadStatus', { message, userId });
  }

  searchMessages(params: { chatHash: string; userId: number | string; term: string }): Observable<ChatMessageSearchOccurrence[]> {
    const userId = Number(params.userId);
    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    this.websocket.sendMessage('searchChatMessages', {
      chatHash: params.chatHash,
      userId,
      term: params.term,
      requestId
    });
    return this.fromWebsocketEvent<ChatMessageSearchResponse>(`returnChatMessageSearch-${userId}`).pipe(
      filter(response => response.requestId === requestId),
      take(1),
      switchMap(response => response.error ? throwError(() => new Error(response.error)) : of(response.occurrences))
    );
  }

  getMessagesFromChat(params: {
    chatHash: string;
    userId: number;
    page: number;
    pageSize: number;
  }): Observable<Message[]> {
    this.websocket.sendMessage('getAllMessages', {
      chatHash: params.chatHash,
      userId: params.userId,
      page: params.page,
      pageSize: params.pageSize
    });
    return this.fromWebsocketEvent<(Message & { createdAt?: string })[]>(
      `returnAllMessages-${params.userId}`
    ).pipe(
      take(1),
      map(messages =>
        this.sortByTimeAscending(messages ?? []).map(msg => ({
          ...msg,
          // Normaliza `time` para o instante real da persistência: o componente
          // renderiza o array na ordem recebida e não reordena por conta
          // própria, então a ordenação e o `time` precisam sair corretos daqui.
          time: new Date(this.parseMessageTime(msg.time, msg.createdAt))
        }))
      )
    );
  }

  uploadFileWithProgress(params: { chatHash: string; file: File; type: string }) {
    const fileFormData = new FormData();
    fileFormData.append('file', params.file);
    const url = `${environment.apiUrl}/chat/send-file/${params.chatHash}/${params.type}`;
    return this.http
      .post<{ fileUrl: string }>(url, fileFormData, {
        reportProgress: true,
        observe: 'events'
      })
      .pipe(
        catchError(error => {
          return throwError(() => error);
        }),
        map((event: any) => {
          if (event.type === HttpEventType.UploadProgress) {
            return { progress: Math.round((event.loaded * 100) / event.total) };
          }
          if (event.type === HttpEventType.Response) {
            return { body: event.body };
          }
          return null;
        })
      );
  }
}
